import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import type { BuscarPecasRow, VehiclePlate } from '@core/types';
import { consultarPlaca } from '../lib/consultas';
import {
  formatarPlaca, normalizarPlaca, placaValida, type TratamentoDaPlaca,
} from '../lib/placa';
import { mensagemDeErro } from '../lib/supabase';
import Aviso from './Aviso';
import BuscaGuiada from './BuscaGuiada';
import type { AcaoNoOndeFica } from './OndeFica';
import FichaDaPlaca from './FichaDaPlaca';

// Busca por placa. A base é da PRÓPRIA loja e é o que sustenta o fluxo: primeira
// visita, o vendedor identifica o carro na busca guiada e salva a placa com um
// toque; da segunda em diante a placa resolve sozinha, de graça e na hora.
//
// A API externa (apiplacas.com.br desde 16/09/2026, créditos pagos) entra só
// para adiantar a PRIMEIRA visita, atrás do agente da VPS (/infra/v1/placa) — o
// token não pode existir no bundle. Sem token configurado, ou fora do ar, a
// resposta é 'indisponivel' e esta tela se comporta exatamente como antes: a
// loja funciona sem a API, e nada aqui depende dela.
//
// DADO DA API SE CONFIRMA. O veículo que vem de fora é palpite do órgão, gravado
// com origem 'api'. Por isso a busca guiada abre com o botão de salvar também
// nesse caso: conferido com o cliente (ou corrigido na cascata), um toque grava
// como 'balcao' e o aviso some. Sem esse botão, um palpite errado ficaria
// gravado para sempre — a tela só oferecia salvar placa desconhecida.
//
// O RESULTADO É UMA FICHA, NÃO UMA LINHA (18/09/2026). Antes aparecia só
// "EMR-8H21 — VW - VolksWagen JETTA 2012", e o dono respondeu que isso não
// serve: quando o documento não determina a versão, o vendedor precisa ver o
// que a consulta disse (combustível, carroceria, as versões FIPE possíveis)
// para saber o que perguntar. FichaDaPlaca mostra o que o sistema concluiu e,
// embaixo, o que a consulta externa retornou.

export default function BuscaPlaca({
  onEscolher, acaoNoOndeFica,
}: {
  onEscolher: (peca: BuscarPecasRow) => void;
  /** O botão da folha "Onde fica", passado adiante à busca por veículo. */
  acaoNoOndeFica?: AcaoNoOndeFica<BuscarPecasRow>;
}) {
  const [texto, setTexto] = useState('');
  const [consultada, setConsultada] = useState<string | null>(null);
  const [veiculo, setVeiculo] = useState<VehiclePlate | null>(null);
  const [tratamento, setTratamento] = useState<TratamentoDaPlaca | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  const consultar = useMutation({
    mutationFn: async () => {
      const placa = normalizarPlaca(texto);
      if (!placaValida(placa)) {
        throw new Error('Placa inválida — use o formato ABC-1234 ou ABC1D23.');
      }
      const r = await consultarPlaca(placa);
      setConsultada(placa);
      setVeiculo(r.veiculo);
      setTratamento(r.tratamento ?? null);
      setAviso(r.aviso ?? null);
    },
  });

  return (
    <div className="space-y-4">
      <div>
        <label className="rotulo" htmlFor="placa">Placa do veículo</label>
        <div className="flex gap-2">
          <input
            id="placa"
            className="campo py-3 font-mono text-lg uppercase tracking-widest"
            placeholder="ABC-1D23"
            value={texto}
            autoComplete="off"
            autoCapitalize="characters"
            spellCheck={false}
            maxLength={8}
            onChange={(e) => {
              setTexto(e.target.value); setConsultada(null); setVeiculo(null); setTratamento(null);
            }}
            onKeyDown={(e) => { if (e.key === 'Enter') consultar.mutate(); }}
          />
          <button type="button" className="botao-primario px-4 py-3 text-sm"
                  disabled={consultar.isPending || !texto.trim()}
                  onClick={() => consultar.mutate()}>
            {consultar.isPending ? '…' : 'Consultar'}
          </button>
        </div>
        <p className="mt-2 text-xs text-dim">
          A base de placas é da própria loja: na primeira visita você identifica o carro e salva;
          da segunda em diante a placa resolve sozinha.
        </p>
      </div>

      {consultar.error ? <Aviso tipo="erro">{mensagemDeErro(consultar.error)}</Aviso> : null}

      {consultada && veiculo && (
        <>
          {/* De onde veio importa no balcão: dado da API é palpite do órgão,
              dado da loja foi conferido por alguém que viu o carro — a ficha
              diz qual dos dois é, e mostra o que a consulta disse. */}
          <FichaDaPlaca placa={consultada} veiculo={veiculo} tratamento={tratamento} />
          <BuscaGuiada
            onEscolher={onEscolher}
            acaoNoOndeFica={acaoNoOndeFica}
            veiculoInicial={veiculo}
            placaParaSalvar={veiculo.origem === 'api' ? consultada : null}
            conferindo={veiculo.origem === 'api'}
          />
        </>
      )}

      {consultada && !veiculo && (
        <>
          <Aviso tipo="alerta">
            {aviso ?? (
              <>
                Placa {formatarPlaca(consultada)} ainda não cadastrada. Identifique o veículo abaixo
                e salve — a próxima consulta será automática.
              </>
            )}
          </Aviso>
          <BuscaGuiada onEscolher={onEscolher} acaoNoOndeFica={acaoNoOndeFica} placaParaSalvar={consultada} />
        </>
      )}
    </div>
  );
}
